/**
 * Normalize hall API / axios errors into a single user-facing string.
 * Handles userMessage, DRF {field: [msg]}, {error}, and nested response.data.
 */
export function getHallApiErrorMessage(err, fallback = 'Something went wrong.') {
  if (!err) return fallback;
  if (typeof err === 'string' && err.trim()) return err.trim();

  const data = err?.response?.data ?? err?.data ?? null;

  const fromUserMessage = coerceMessage(err?.userMessage);
  if (fromUserMessage) return fromUserMessage;

  const fromTopError = coerceMessage(data?.error) || coerceMessage(data?.detail) || coerceMessage(data?.message);
  if (fromTopError) return fromTopError;

  const fromFields = firstFieldError(data);
  if (fromFields) return fromFields;

  // Some callers stash field errors directly on the error object
  const fromErrFields = firstFieldError(err);
  if (fromErrFields) return fromErrFields;

  const fromMessage = coerceMessage(err?.message);
  if (fromMessage && !/^request failed with status code/i.test(fromMessage)) {
    return fromMessage;
  }

  return fallback;
}

function coerceMessage(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const msg = coerceMessage(item);
      if (msg) return msg;
    }
    return '';
  }
  if (typeof value === 'object') {
    if (typeof value.string === 'string') return value.string.trim();
    return '';
  }
  return String(value).trim();
}

function firstFieldError(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return '';
  const skip = new Set([
    'userMessage',
    'status',
    'statusCode',
    'config',
    'request',
    'response',
    'stack',
    'name',
    'message',
    'code',
    'isAxiosError',
  ]);
  for (const [key, value] of Object.entries(payload)) {
    if (skip.has(key)) continue;
    const msg = coerceMessage(value);
    if (msg) return msg;
  }
  return '';
}
